import {
	DecisionApprovalStep,
	DecisionNoteSection,
	DecisionSignatureStatus,
	DecisionSignerRole,
	DecisionStepAction,
	RequestStatusStage,
} from "@azm/features/requests/enums";
import type {
	IRequestAttachmentInfo,
	IRequestDecision,
	IRequestDecisionSignature,
	IRequestDecisionStep,
} from "@azm/features/requests/interfaces";
import type { Dropdown } from "@azm/shared/interfaces";

const HOUR_MS = 60 * 60 * 1000;

function hoursAgo(hours: number): string {
	return new Date(Date.now() - hours * HOUR_MS).toISOString();
}

export const MOCK_DECISION_SCENARIO: {
	stage: RequestStatusStage | null;
	viewerParticipantRoleId: DecisionSignerRole | null;
	viewerIsDrafter: boolean;
} = {
	stage: RequestStatusStage.DecisionDrafting,
	//stage: RequestStatusStage.FirstMemberNotes, //كرت الملاحظات والإرجاع
	//stage: RequestStatusStage.SecondMemberNotes, //أعاده الرئيس للعضو الثاني: لوحة سبب الإعادة + ملاحظات الأعضاء (مع viewerParticipantRoleId: SecondMember)
	//stage: RequestStatusStage.PendingCommitteeChairApproval, //كرت الرئيس
	//stage: RequestStatusStage.PreliminaryDecisionIssued, //التواقيع
	//stage: RequestStatusStage.FinalDecisionIssued, //نسخة القرار

	viewerIsDrafter: true,
	//كرت الملاحظات والإرجاع
	viewerParticipantRoleId: DecisionSignerRole.FirstMember,
	//كرت الرئيس
	//viewerParticipantRoleId: DecisionSignerRole.CommitteeChairman,
};

/** Lookup.DecisionNoteSections ("موضع الملاحظة" options of the return dialog). */
export const MOCK_DECISION_NOTE_SECTIONS: Dropdown[] = [
	{ id: DecisionNoteSection.Facts, text: "القرار — الوقائع" },
	{ id: DecisionNoteSection.Reasons, text: "القرار — الأسباب" },
	{ id: DecisionNoteSection.Verdict, text: "القرار — منطوق القرار" },
	{ id: DecisionNoteSection.Penalty, text: "القرار — الجزاء" },
	{ id: DecisionNoteSection.MemberNotes, text: "القرار — ملاحظات الأعضاء" },
];

export const MOCK_PENALTY_TYPES: Dropdown[] = [
	{ id: 1, text: "الإنذار" },
	{ id: 2, text: "اللوم" },
	{ id: 3, text: "غرامة مالية" },
	{ id: 4, text: "الإيقاف عن مزاولة المهنة مدة لا تزيد على ثلاث سنوات" },
	{ id: 5, text: "شطب الاسم من جدول المحامين وإلغاء الترخيص" },
];

function attachment(id: string, fileName: string, kb: number): IRequestAttachmentInfo {
	return { attachmentId: id, fileName, contentType: "application/pdf", fileSize: kb * 1024 };
}

/**
 * Approval path: drafted, both members approved with notes, the chair returned it to the second member
 * (two return notes with attachments), then the second member and the chair approved.
 */
const ALL_STEPS: IRequestDecisionStep[] = [
	{
		stepId: "1",
		approvalStepId: DecisionApprovalStep.Drafting,
		actionId: DecisionStepAction.Submitted,
		performedByName: "سامر خالد العتيبي",
		note: null,
		returnNotes: [],
		decisionVersion: 1,
		performedAt: hoursAgo(72),
	},
	{
		stepId: "2",
		approvalStepId: DecisionApprovalStep.FirstMemberNotes,
		actionId: DecisionStepAction.Approved,
		performedByName: "أ. سعود ناصر الدوسري",
		note: "أتفق مع ما انتهى إليه مشروع القرار، وأوصي بمراعاة الظروف المخفِّفة والمشدِّدة عند تقدير الجزاء، والإشارة إلى سوابق المخالف إن وُجدت.",
		returnNotes: [],
		decisionVersion: 1,
		performedAt: hoursAgo(60),
	},
	{
		stepId: "3",
		approvalStepId: DecisionApprovalStep.SecondMemberNotes,
		actionId: DecisionStepAction.Approved,
		performedByName: "أ. لطيفة خالد العمري",
		note: "أوافق على المنطوق ونوع الجزاء.",
		returnNotes: [],
		decisionVersion: 1,
		performedAt: hoursAgo(40),
	},
	{
		stepId: "4",
		approvalStepId: DecisionApprovalStep.ChairApproval,
		actionId: DecisionStepAction.Returned,
		performedByName: "د. عبدالرحمن فهد المالكي",
		note: null,
		returnNotes: [
			{
				sectionId: DecisionNoteSection.Facts,
				sectionName: "القرار — الوقائع",
				reason: "يرجى تضمين الوقائع تاريخ استلام الأتعاب وقيمتها كما وردت في محضر التحقيق.",
				attachments: [attachment("mock-ret-1", "مرفق_الملاحظة_1_1.pdf", 243), attachment("mock-ret-2", "مرفق_الملاحظة_1_2.pdf", 212)],
			},
			{
				sectionId: DecisionNoteSection.Reasons,
				sectionName: "القرار — الأسباب",
				reason: "يرجى الإشارة إلى المادة النظامية التي تم الإخلال بها في الأسباب.",
				attachments: [attachment("mock-ret-3", "مرفق_الملاحظة_2.pdf", 347)],
			},
		],
		decisionVersion: 1,
		performedAt: hoursAgo(20),
	},
	{
		stepId: "5",
		approvalStepId: DecisionApprovalStep.SecondMemberNotes,
		actionId: DecisionStepAction.Approved,
		performedByName: "أ. لطيفة خالد العمري",
		note: "تمت مراجعة ملاحظات الرئيس ولا مانع من اعتماد القرار.",
		returnNotes: [],
		decisionVersion: 2,
		performedAt: hoursAgo(10),
	},
	{
		stepId: "6",
		approvalStepId: DecisionApprovalStep.ChairApproval,
		actionId: DecisionStepAction.Approved,
		performedByName: "د. عبدالرحمن فهد المالكي",
		note: "بعد مراجعة مشروع القرار وملاحظات العضوين، أرى أنه مستوفٍ لأركانه النظامية وأسبابه ومنطوقه، وصالح للاعتماد والتوقيع.",
		returnNotes: [],
		decisionVersion: 2,
		performedAt: hoursAgo(8),
	},
];

/** How many of ALL_STEPS have happened at each stage (SecondMemberNotes is the "returned by the chair" case). */
const STEPS_BY_STAGE: Partial<Record<RequestStatusStage, number>> = {
	[RequestStatusStage.DecisionDrafting]: 0,
	[RequestStatusStage.FirstMemberNotes]: 1,
	[RequestStatusStage.SecondMemberNotes]: 4,
	[RequestStatusStage.PendingCommitteeChairApproval]: 5,
	[RequestStatusStage.PreliminaryDecisionIssued]: 6,
	[RequestStatusStage.FinalDecisionIssued]: 6,
};

function signature(
	participantRoleId: DecisionSignerRole,
	signerName: string,
	signedHoursAgo: number | null,
	verificationCode: string,
): IRequestDecisionSignature {
	const signed = signedHoursAgo !== null;
	return {
		participantRoleId,
		signerName,
		signatureStatusId: signed ? DecisionSignatureStatus.Signed : DecisionSignatureStatus.Pending,
		signedAt: signed ? hoursAgo(signedHoursAgo) : null,
		verificationCode: signed ? verificationCode : null,
	};
}

function mockSignatures(stage: RequestStatusStage): IRequestDecisionSignature[] {
	if (stage !== RequestStatusStage.PreliminaryDecisionIssued && stage !== RequestStatusStage.FinalDecisionIssued) return [];
	const isFinal = stage === RequestStatusStage.FinalDecisionIssued;
	return [
		signature(DecisionSignerRole.CommitteeChairman, "د. عبدالرحمن فهد المالكي", 20, "SIG-7F3A-29C1"),
		signature(DecisionSignerRole.FirstMember, "أ. سعود ناصر الدوسري", 10, "SIG-4B8E-11D7"),
		signature(DecisionSignerRole.SecondMember, "أ. لطيفة خالد العمري", isFinal ? 2 : null, "SIG-9C2D-63A4"),
	];
}

type DecisionText = Pick<
	IRequestDecision,
	"preamble" | "facts" | "reasons" | "verdict" | "penaltyTypeId" | "penaltyTypeName" | "penaltyDetails"
>;

const EMPTY_TEXT: DecisionText = {
	preamble: null,
	facts: null,
	reasons: null,
	verdict: null,
	penaltyTypeId: null,
	penaltyTypeName: null,
	penaltyDetails: null,
};

const FILLED_TEXT: DecisionText = {
	preamble:
		"فإن لجنة التأديب (اللجنة الأولى) المشكّلة بموجب نظام المحاماة ولائحته التنفيذية، وبعد اطلاعها على الشكوى المقيّدة برقم القيد الموحد 144604352290 المحال إليها من قسم الادعاء بموجب لائحة الادعاء المؤرخة في 12/07/2026م بحق المحامي محمود سالم مصطفى العلي (ترخيص رقم م-4930)، وعلى المذكرات المتبادلة ومحاضر الجلسات والمستندات المرفقة، أصدرت القرار الآتي:",
	facts:
		"تتحصّل وقائع الموضوع فيما ورد في لائحة الادعاء من نسبة مخالفة مهنية إلى المشتكى عليه، وحاصلها: استلام المحامي أتعاباً كاملة دون مباشرة الدعوى أمام المحكمة المختصة، أحيل بموجب لائحة ادعاء. وقد أُشعر المشتكى عليه بالإحالة، وقدّم مذكرة رد أبدى فيها دفوعه، وعقّب المدعي متمسكاً بلائحة الادعاء. وعقدت اللجنة جلسة واحدة حضرها محمود سالم مصطفى العلي، أ. ماجد سعد الحربي، د. عبدالرحمن فهد المالكي، أ. سعود ناصر الدوسري، أ. لطيفة خالد العمري، وتمكّن المشتكى عليه من إبداء دفاعه.",
	reasons:
		"وحيث إن اللجنة بعد دراسة الأوراق والاطلاع على الدراسة القانونية، ثبت لديها قيام المخالفة المنسوبة إلى المشتكى عليه بما ورد في لائحة الادعاء ومحاضر التحقيق والمستندات المرفقة، وأن ما أبداه من دفوع جاء مرسلاً لا يدحض الأدلة المقدّمة، وحيث إن ذلك يُعد إخلالاً بواجبات المحامي المقرّرة في نظام المحاماة ولائحته التنفيذية، وبالنظر إلى جسامة المخالفة وظروفها وعدم ثبوت سوابق مماثلة، فإن اللجنة تنتهي إلى ما سيرد في منطوق هذا القرار.",
	verdict:
		"أولاً: ثبوت المخالفة المنسوبة إلى المحامي محمود سالم مصطفى العلي (ترخيص رقم م-4930).\nثانياً: إيقاع الجزاء المبيّن أدناه بحقه.\nثالثاً: إبلاغ الأطراف بهذا القرار، وللمشتكى عليه حق الاعتراض عليه خلال المدة النظامية.",
	penaltyTypeId: 1,
	penaltyTypeName: "الإنذار",
	penaltyDetails: null,
};

export function createMockRequestDecision(
	stage: RequestStatusStage,
	viewerParticipantRoleId: DecisionSignerRole | null,
): IRequestDecision {
	const steps = ALL_STEPS.slice(0, STEPS_BY_STAGE[stage] ?? 0);
	const isIssued = stage === RequestStatusStage.PreliminaryDecisionIssued || stage === RequestStatusStage.FinalDecisionIssued;
	const isFinal = stage === RequestStatusStage.FinalDecisionIssued;
	const isFreshDraft = steps.length === 0;
	return {
		decisionId: "mock-decision-1",
		code: "DEC-2026-01",
		decisionNumber: "01",
		decisionYear: 2026,
		stage,
		canEdit: false,
		canReview: false,
		canApprove: false,
		canSign: false,
		defendantName: "محمود سالم مصطفى العلي",
		defendantLicenseNumber: "م-4930",
		sessionDate: "2026-09-05T12:30:00",
		violationName: "مخالفة مهنية",
		committeeMembers: [
			{ name: "د. عبدالرحمن فهد المالكي", participantRoleId: 4 },
			{ name: "أ. سعود ناصر الدوسري", participantRoleId: 5 },
			{ name: "أ. لطيفة خالد العمري", participantRoleId: 6 },
			{ name: "أ. ماجد سعد الحربي", participantRoleId: 3 },
		],
		...(isFreshDraft ? EMPTY_TEXT : FILLED_TEXT),
		version: stage === RequestStatusStage.DecisionDrafting ? 1 : 2,
		initialIssuedAt: isIssued ? hoursAgo(20) : null,
		finalIssuedAt: isFinal ? hoursAgo(2) : null,
		viewerParticipantRoleId,
		qrCodeImage: null,
		steps,
		signatures: mockSignatures(stage),
		aiCall: null,
	};
}
